import type { PermissionSpec } from './types';

/**
 * Permission evaluation.
 *
 * A UX AFFORDANCE, NOT AUTHORIZATION. Nothing here grants anything. Every
 * request is re-authorized by the backend on every call, and the server is the
 * only security boundary in this system (design spec §10). A user who bypasses
 * any check in this file gains a button and nothing else.
 *
 * The checks are therefore allowed to be simple. They exist so the interface
 * does not lie to an operator by offering an action that will be refused, not
 * to keep a record out of reach.
 */

/**
 * All-of. An empty list is vacuously satisfied — that is what makes `require`
 * omittable, not a special case handled at the call sites.
 */
function grantsAll(granted: Set<string>, required: string[]): boolean {
  return required.every((code) => granted.has(code));
}

/**
 * Any-of. An empty list is satisfied too: "at least one of nothing" cannot be
 * met by any session, and denying everyone because a field was left out would
 * be the most dangerous possible default for an authorization check.
 */
function grantsAny(granted: Set<string>, required: string[]): boolean {
  return required.length === 0 || required.some((code) => granted.has(code));
}

/**
 * Decides whether a session's granted permission codes satisfy a spec.
 *
 * Both conditions must hold, so the two fields compose rather than override:
 * `{ require: ['a'], requireAny: ['b', 'c'] }` means "a, plus at least one of
 * b or c". Both fields empty or absent ⇒ allowed.
 *
 * `granted` is turned into a Set once so the lookup stays O(1) per code. The
 * sets involved are tens of entries, so this is correctness hygiene rather
 * than a performance requirement.
 */
export function checkPermissions(
  granted: ReadonlyArray<string>,
  spec: PermissionSpec,
): boolean {
  const held = new Set(granted);
  return grantsAll(held, spec.require ?? []) && grantsAny(held, spec.requireAny ?? []);
}
